import type { CSSProperties } from 'react'

type InlineRichTextStyle = {
  bold?: boolean
  italic?: boolean
  underline?: boolean
  color?: string
}

type InlineRichTextSegment = {
  text: string
  style: InlineRichTextStyle
}

const getNumericFontWeight = (value?: CSSProperties['fontWeight']) => {
  if (typeof value === 'number') {
    return value
  }

  if (typeof value !== 'string') {
    return undefined
  }

  if (/^\d+$/.test(value)) {
    return Number(value)
  }

  if (value === 'bold' || value === 'bolder') {
    return 700
  }

  if (value === 'normal' || value === 'lighter') {
    return 400
  }

  return undefined
}

const getBoldFontWeight = (baseFontWeight?: CSSProperties['fontWeight']) => {
  const numericWeight = getNumericFontWeight(baseFontWeight)
  return numericWeight !== undefined && numericWeight >= 600 ? 900 : 700
}

const isSafeColor = (value: string) => {
  const color = value.trim()
  if (/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color)) {
    return true
  }

  const rgbMatch = /^rgba?\((.+)\)$/.exec(color)
  if (!rgbMatch) {
    return false
  }

  const parts = rgbMatch[1].split(',').map((part) => part.trim())
  if (parts.length !== 3 && parts.length !== 4) {
    return false
  }

  const rgb = parts.slice(0, 3).map(Number)
  if (rgb.some((part) => !Number.isInteger(part) || part < 0 || part > 255)) {
    return false
  }

  if (parts.length === 4) {
    const alpha = Number(parts[3])
    return Number.isFinite(alpha) && alpha >= 0 && alpha <= 1
  }

  return true
}

const pushText = (
  segments: InlineRichTextSegment[],
  text: string,
  style: InlineRichTextStyle
) => {
  if (!text) return
  const normalizedText = text.replace(/\s*\n+\s*/g, ' ')
  if (!normalizedText) return

  const previous = segments[segments.length - 1]
  if (
    previous &&
    previous.style.bold === style.bold &&
    previous.style.italic === style.italic &&
    previous.style.underline === style.underline &&
    previous.style.color === style.color
  ) {
    previous.text += normalizedText
    return
  }

  segments.push({ text: normalizedText, style: { ...style } })
}

const parseRange = (
  value: string,
  style: InlineRichTextStyle
): InlineRichTextSegment[] => {
  const segments: InlineRichTextSegment[] = []
  let index = 0

  while (index < value.length) {
    const rest = value.slice(index)
    const lowerRest = rest.toLowerCase()

    if (rest.startsWith('**')) {
      const endIndex = value.indexOf('**', index + 2)
      if (endIndex > index + 2) {
        segments.push(
          ...parseRange(value.slice(index + 2, endIndex), {
            ...style,
            bold: true,
          })
        )
        index = endIndex + 2
        continue
      }
    }

    if (rest.startsWith('*') && !rest.startsWith('**')) {
      const endIndex = value.indexOf('*', index + 1)
      if (endIndex > index + 1) {
        segments.push(
          ...parseRange(value.slice(index + 1, endIndex), {
            ...style,
            italic: true,
          })
        )
        index = endIndex + 1
        continue
      }
    }

    if (lowerRest.startsWith('<u>')) {
      const endIndex = lowerRest.indexOf('</u>')
      if (endIndex > 3) {
        segments.push(
          ...parseRange(rest.slice(3, endIndex), {
            ...style,
            underline: true,
          })
        )
        index += endIndex + 4
        continue
      }
    }

    const spanMatch =
      /^<span\s+style=(["'])\s*color\s*:\s*([^;"']+)\s*;?\s*\1\s*>/i.exec(rest)
    if (spanMatch) {
      const openLength = spanMatch[0].length
      const endIndex = lowerRest.indexOf('</span>', openLength)
      if (endIndex > openLength) {
        const color = spanMatch[2].trim()
        segments.push(
          ...parseRange(rest.slice(openLength, endIndex), {
            ...style,
            color: isSafeColor(color) ? color : style.color,
          })
        )
        index += endIndex + 7
        continue
      }
    }

    const nextMarkers = ['**', '*', '<u>', '<U>', '<span', '<SPAN']
      .map((marker) => value.indexOf(marker, index + 1))
      .filter((nextIndex) => nextIndex > index)
    const nextIndex = nextMarkers.length
      ? Math.min(...nextMarkers)
      : value.length
    pushText(segments, value.slice(index, nextIndex), style)
    index = nextIndex
  }

  return segments
}

const parseInlineRichText = (value: string) => parseRange(value, {})

type InlineRichTextProps = {
  text: string
  fallback?: string
  className?: string
  style?: CSSProperties
  baseFontWeight?: CSSProperties['fontWeight']
}

export default function InlineRichText({
  text,
  fallback = '',
  className,
  style,
  baseFontWeight,
}: InlineRichTextProps) {
  const segments = parseInlineRichText(text.trim() || fallback)
  const boldFontWeight = getBoldFontWeight(baseFontWeight ?? style?.fontWeight)

  return (
    <span className={className} style={style}>
      {segments.map((segment, index) => (
        <span
          key={`${segment.text}-${index}`}
          style={{
            fontWeight: segment.style.bold ? boldFontWeight : undefined,
            fontStyle: segment.style.italic ? 'italic' : undefined,
            textDecorationLine: segment.style.underline
              ? 'underline'
              : undefined,
            color: segment.style.color,
          }}
        >
          {segment.text}
        </span>
      ))}
    </span>
  )
}
